import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, Award, TrendingUp, ArrowLeft, Loader2, Users, Mail, Trash2, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AVATAR_COLORS = [
  'bg-[#B91C1C]', 'bg-[#0A0A0A]', 'bg-[#7F1D1D]', 'bg-[#171717]',
  'bg-[#9A3412]', 'bg-[#1E3A8A]', 'bg-[#065F46]', 'bg-[#5B21B6]'
];

const avatarSrc = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
};

const CompanyVerifiedShortlistPage = () => {
  const navigate = useNavigate();
  const [developers, setDevelopers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState('');

  const token = () => localStorage.getItem('company_token');

  const clearAuthAndRedirect = () => {
    localStorage.removeItem('company_token');
    localStorage.removeItem('company_data');
    navigate('/company/auth');
  };

  const fetchShortlist = async () => {
    if (!token()) { navigate('/company/auth'); return; }
    setLoading(true);
    setLoadError(false);
    try {
      const res = await axios.get(`${API}/company/talent-shortlist`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      setDevelopers(res.data.developers || []);
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        toast.error('Your company session expired. Please log in again.');
        clearAuthAndRedirect();
        return;
      }
      setLoadError(true);
      toast.error('Could not load your shortlist');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShortlist();
  }, []);

  const removeDev = async (dev) => {
    setBusy(dev.student_id);
    try {
      await axios.delete(`${API}/company/talent-shortlist/${dev.student_id}`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      setDevelopers((prev) => prev.filter((d) => d.student_id !== dev.student_id));
      toast.success(`${dev.name} removed from shortlist`);
    } catch (err) {
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        clearAuthAndRedirect();
        return;
      }
      toast.error('Could not remove developer');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="min-h-screen bg-[#f3f2ef]" data-testid="company-verified-shortlist-page">
      <div className="max-w-5xl mx-auto px-6 py-10">
        <button
          onClick={() => navigate('/company/dashboard')}
          className="inline-flex items-center gap-2 text-[13px] font-bold text-[#525252] hover:text-[#B91C1C] transition-colors mb-6"
          data-testid="back-dashboard-btn"
        >
          <ArrowLeft size={16} /> Back to Dashboard
        </button>

        <div className="flex items-center gap-3 mb-2">
          <div className="w-11 h-11 rounded-xl bg-[#FEF2F2] flex items-center justify-center">
            <Shield size={20} className="text-[#B91C1C]" fill="currentColor" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-[#0A0A0A]">Verified Talent Shortlist</h1>
            <p className="text-[14px] text-[#525252]">Developers you have saved from the verified talent directory.</p>
          </div>
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="flex items-center justify-center py-24 text-[#737373]" data-testid="shortlist-loading">
              <Loader2 size={28} className="animate-spin" />
            </div>
          ) : loadError ? (
            <div className="flex flex-col items-center justify-center py-24 text-center bg-white rounded-2xl border border-[#E5E5E5]" data-testid="shortlist-load-error">
              <div className="w-16 h-16 rounded-full bg-[#FEF2F2] border border-[#E5E5E5] flex items-center justify-center mb-6">
                <Users size={26} className="text-[#B91C1C]" />
              </div>
              <p className="text-lg font-bold text-[#171717] mb-1">Couldn't load your shortlist</p>
              <p className="text-[#737373] max-w-sm mb-6">Something went wrong. Please try again.</p>
              <button
                onClick={fetchShortlist}
                className="inline-flex items-center gap-2 bg-[#171717] text-white px-6 py-3 rounded-full font-bold text-[14px] hover:bg-[#0A0A0A] transition-all"
                data-testid="shortlist-retry"
              >
                Retry
              </button>
            </div>
          ) : developers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center bg-white rounded-2xl border border-[#E5E5E5]" data-testid="shortlist-empty">
              <div className="w-16 h-16 rounded-full bg-[#FAFAF9] border border-[#E5E5E5] flex items-center justify-center mb-6">
                <Users size={26} className="text-[#A3A3A3]" />
              </div>
              <p className="text-lg font-bold text-[#171717] mb-1">No shortlisted developers yet</p>
              <p className="text-[#737373] max-w-sm mb-6">Browse the verified talent directory and shortlist developers you would like to reach out to.</p>
              <button
                onClick={() => navigate('/verified-developers')}
                className="inline-flex items-center gap-2 bg-[#171717] text-white px-6 py-3 rounded-full font-bold text-[14px] hover:bg-[#0A0A0A] transition-all"
                data-testid="browse-directory-btn"
              >
                Browse verified developers
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-[13px] font-bold uppercase tracking-wider text-[#525252]" data-testid="shortlist-count">
                {developers.length} developer{developers.length === 1 ? '' : 's'} shortlisted
              </p>
              {developers.map((dev, idx) => {
                const src = avatarSrc(dev.avatar_url);
                return (
                  <div
                    key={dev.student_id}
                    className="bg-white rounded-2xl border border-[#E5E5E5] p-5 flex flex-col sm:flex-row sm:items-center gap-4"
                    data-testid={`shortlist-card-${idx}`}
                  >
                    {src ? (
                      <img
                        src={src}
                        alt={dev.name}
                        onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextElementSibling.style.display = 'flex'; }}
                        className="w-14 h-14 rounded-full object-cover shrink-0"
                      />
                    ) : null}
                    <div
                      className={`w-14 h-14 rounded-full ${AVATAR_COLORS[idx % AVATAR_COLORS.length]} items-center justify-center text-white font-extrabold text-[17px] shrink-0`}
                      style={{ display: src ? 'none' : 'flex' }}
                    >
                      {dev.initials}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="font-extrabold text-[#0A0A0A] text-[16px] truncate">{dev.name}</p>
                        {dev.is_verified && <Shield size={15} className="text-[#B91C1C] shrink-0" fill="currentColor" />}
                      </div>
                      {dev.college && <p className="text-[13px] text-[#737373] truncate">{dev.college}</p>}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                        {dev.national_rank && (
                          <span className="inline-flex items-center gap-1 text-[12px] font-bold text-[#171717]">
                            <TrendingUp size={13} className="text-[#B91C1C]" /> Rank #{dev.national_rank}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#B91C1C]">
                          <Award size={13} /> {dev.plan_name}
                        </span>
                        {dev.skills && dev.skills.length > 0 && (
                          <span className="text-[12px] text-[#525252] truncate">{dev.skills.slice(0, 4).join(' · ')}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {dev.email && (
                        <a
                          href={`mailto:${dev.email}`}
                          className="inline-flex items-center gap-2 bg-[#171717] text-white px-4 py-2.5 rounded-full font-bold text-[13px] hover:bg-[#0A0A0A] transition-all"
                          data-testid={`contact-btn-${idx}`}
                        >
                          <Mail size={15} /> Contact
                        </a>
                      )}
                      {dev.profile_url && (
                        <button
                          onClick={() => navigate(dev.profile_url)}
                          className="inline-flex items-center gap-2 border border-[#E5E5E5] px-4 py-2.5 rounded-full font-bold text-[13px] text-[#171717] hover:border-[#B91C1C] hover:text-[#B91C1C] transition-all"
                          data-testid={`view-profile-btn-${idx}`}
                        >
                          <ExternalLink size={15} /> Profile
                        </button>
                      )}
                      <button
                        onClick={() => removeDev(dev)}
                        disabled={busy === dev.student_id}
                        className="w-10 h-10 rounded-full border border-[#E5E5E5] flex items-center justify-center text-[#B91C1C] hover:bg-[#FEF2F2] hover:border-[#B91C1C]/40 transition-all disabled:opacity-60"
                        data-testid={`remove-btn-${idx}`}
                        aria-label={`Remove ${dev.name} from shortlist`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyVerifiedShortlistPage;
